import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('token');
    
    if (token) {
      // Fetch current user data
      api.get('/users/me')
        .then(response => {
          setCurrentUser(response.data);
        })
        .catch(err => {
          console.error('Error fetching user data:', err);
          // Token might be expired or invalid
          localStorage.removeItem('token');
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  // Register user
  const register = async (name, email, username, password, telegram_username = null) => {
    try {
      setError('');
      const response = await api.post('/auth/register', {
        name,
        email,
        username,
        password,
        telegram_username
      });
      
      return response.data;
    } catch (err) {
      console.error('Registration error:', err);
      // Handle error properly - store only string message
      setError(typeof err.response?.data?.detail === 'string' 
        ? err.response.data.detail 
        : 'Failed to register');
      throw err;
    }
  };

  // Login user
  const login = async (email, password) => {
    try {
      setError('');
      
      // Create URLSearchParams for form data
      const formData = new URLSearchParams();
      formData.append('username', email);
      formData.append('password', password);
      
      // Send with application/x-www-form-urlencoded content type
      const response = await api.post('/auth/login', formData.toString(), {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      });
      
      // Save token to localStorage
      localStorage.setItem('token', response.data.access_token);
      
      // Get user data
      const userResponse = await api.get('/users/me');
      setCurrentUser(userResponse.data);
      
      return userResponse.data;
    } catch (err) {
      console.error('Login error:', err);
      // Handle error properly - store only string message
      setError(typeof err.response?.data?.detail === 'string' 
        ? err.response.data.detail 
        : 'Failed to login');
      throw err;
    }
  };

  // Logout user
  const logout = () => {
    localStorage.removeItem('token');
    setCurrentUser(null);
  };

  const value = {
    currentUser,
    isAuthenticated: !!currentUser,
    loading,
    error: typeof error === 'string' ? error : 'An error occurred',
    register,
    login,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
} 